import * as React from 'react';
import { FC } from 'react';
import { classes, style, stylesheet } from 'typestyle';
import {
  Black,
  BlackOpacity04,
  Blue80,
  ButtonIcon,
  Gray100,
  Gray30,
  typeCompact12RegularProperties,
  typeCompact14MediumProperties,
  typeMono14MediumProperties,
} from '@traceair/tds';
import {
  ExpandLessIcon,
  ExpandMoreIcon,
  SurfaceAreaIcon,
  PanoramaIcon,
  OriginalGroundIcon,
  GCPIcon,
  GCPIconState,
  GISMarkerIconsPlace,
  RadiusIcon,
} from '@traceair/webapp-icons';
import { InvoicingSection } from '../types';
import { getSectionSubtitle } from '../InvoicingUtils';

const css = stylesheet({
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 24px 12px 20px',
    cursor: 'pointer',
    userSelect: 'none',
    height: 32,
    boxSizing: 'content-box' as const,
    borderBottom: `1px solid ${Gray30}`,
    $nest: {
      '&:hover': {
        backgroundColor: BlackOpacity04,
      },
    },
  },
  title: {
    flex: 1,
    marginLeft: 12,
  },
  label: {
    margin: 0,
    ...typeCompact14MediumProperties,
    color: Black,
  },
  subtitle: {
    margin: 0,
    ...typeCompact12RegularProperties,
    color: Gray100,
  },
  total: {
    justifyContent: 'flex-end',
  },
  amount: {
    margin: 0,
    ...typeMono14MediumProperties,
    color: Black,
  },
  amountZero: {
    color: Gray100,
    ...typeMono14MediumProperties,
  },
});

// HACK This is a temporary fix to make the TDS GCP icon smaller in the section header.
const gcpIconClass = style({ $nest: { '& svg[data-icon-id="GCPIcon"]': { width: 16, height: 16 } } });

function getCategoryIcon(category: string): React.ReactElement {
  switch (category) {
    case 'scan':
      return <SurfaceAreaIcon size={16} color={Blue80} />;
    case 'pano':
      return <PanoramaIcon size={16} color={Blue80} />;
    case 'lidar':
      return <OriginalGroundIcon size={16} color={Blue80} />;
    case 'gcp_services':
    case 'gcp_materials':
      return <GCPIcon state={GCPIconState.Active} place={GISMarkerIconsPlace.Common} />;
    default:
      return <RadiusIcon size={16} color={Blue80} />;
  }
}

export const SectionIcon: FC<{ category: string; isExpanded: boolean; rowHovered?: boolean }> = ({
  category,
  isExpanded,
  rowHovered,
}) => {
  const icon =
    !isExpanded && rowHovered ? (
      <ExpandLessIcon size={16} color={Blue80} />
    ) : isExpanded ? (
      <ExpandMoreIcon size={16} color={Blue80} />
    ) : (
      getCategoryIcon(category)
    );
  return <ButtonIcon icon={icon} size="xs" style="secondary" className={gcpIconClass} />;
};

type SectionHeaderProps = {
  section: InvoicingSection;
  isExpanded: boolean;
  onToggle: () => void;
  subtitle?: string;
  amountNode?: React.ReactNode;
  className?: string;
};

export const SectionHeader: FC<SectionHeaderProps> = ({
  section,
  isExpanded,
  onToggle,
  subtitle,
  amountNode,
  className,
}) => {
  const [rowHovered, setRowHovered] = React.useState(false);
  return (
    <div
      className={classes(css.header, className)}
      onClick={onToggle}
      onMouseEnter={() => setRowHovered(true)}
      onMouseLeave={() => setRowHovered(false)}
    >
      <SectionIcon category={section.id} isExpanded={isExpanded} rowHovered={rowHovered} />
      <div className={css.title}>
        <p className={css.label}>{section.label}</p>
        {(() => {
          const sub = subtitle ?? getSectionSubtitle(section);
          return sub ? <p className={css.subtitle}>{sub}</p> : null;
        })()}
      </div>
      {amountNode != null ? (
        <div onClick={(e) => e.stopPropagation()}>{amountNode}</div>
      ) : (
        <div className={css.total}>
          <p className={`${css.amount} ${section.total === 0 ? css.amountZero : ''}`}>
            {section.total !== undefined ? `$${section.total.toLocaleString()}` : ''}
          </p>
        </div>
      )}
    </div>
  );
};
